import {
  Backdrop,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { DatePicker } from '@mui/lab';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { useSnackbar } from 'notistack';
import { paramCase } from 'change-case';
import { useMemo, useState } from 'react';
import getFileData from 'src/utils/getFileData';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { UploadFiles } from 'src/careflow/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { InsuredOptionProps } from 'src/@types/crs/referral';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { isEqual, lowerCase, startCase } from 'lodash';
import { DocumentReference } from 'src/careflow/ph-core/fhir-client/resources';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  addDocumentRelationships,
  convertBlobToBase64,
  COVERAGE_CLASS_CODE,
  COVERAGE_CLASS_SYSTEM,
  getCoverageReferences,
  getRelatedReferences,
  getResourceReference,
  hasAllReferences,
  mapDocumentReferenceToResource,
  removeDocumentRelationships,
} from './helpers';

type FormValues = {
  insuranceId: string;
  insuranceCompany: string;
  expirationDate: Date;
  files: Blob[];
  documents: string[];
};

type Props = InsuredOptionProps;

export default function Commercial({
  option,
  patient,
  documentReference,
  coverage,
  serviceRequest,
  handleCreateBynary,
  handleCoverage,
  handleCreateDocumentReference,
  handleUpdateDocumentReference,
  handleUpdateResource,
  onCancel,
  i18n,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const authUser = useAuth().getCurrentUser();
  const { configurations } = useTenantConfigData();

  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [organization] = useOrganizations();

  const EventSchema = Yup.object({
    insuranceId: Yup.string().required(
      i18n('patients.details.insurance.policeNumberRequired', 'crs')
    ),
    insuranceCompany: Yup.string().required(
      i18n('patients.details.insurance.insuranceCompanyRequired', 'crs')
    ),
    groupNumber: Yup.string().required(
      i18n('patients.details.insurance.groupNumberRequired', 'crs')
    ),
    expirationDate: Yup.date().required(
      i18n('patients.details.insurance.expirationDateRequired', 'crs')
    ),
  });

  const commercial = useMemo(() => {
    if (paramCase(coverage?.class?.[0].name || '') === 'commercial-medicaid') {
      return 2;
    }

    if (coverage?.class?.[0].name) {
      return 1;
    }

    return 0;
  }, [coverage]);

  /**
   * --------------------------------------------------------------------------
   * Related documents
   * --------------------------------------------------------------------------
   *
   * If both ServiceRequest and Coverage exist, a document is considered
   * related only when it contains both references.
   */

  const documentsRelated = useMemo<string[]>(() => {
    const serviceRequestReference = getResourceReference(serviceRequest);

    const coverageReference = getResourceReference(coverage);

    const requiredReferences = [serviceRequestReference, coverageReference].filter(
      (reference): reference is string => Boolean(reference)
    );

    if (!requiredReferences.length) {
      return [];
    }

    return (
      documentReference
        ?.filter((document) => {
          const related = document.context?.related ?? [];

          return hasAllReferences(related, requiredReferences);
        })
        .map((document) => document.id)
        .filter((id): id is string => Boolean(id)) ?? []
    );
  }, [documentReference, serviceRequest, coverage]);

  const defaultValues = useMemo(
    () => ({
      insuranceId: coverage?.subscriberId || '',
      insuranceCompany: coverage?.payor?.[0].display?.split('_')[commercial] || '',
      groupNumber: coverage?.class?.[0].value || '',
      expirationDate: coverage?.period?.end || new Date(),
      documents: documentsRelated || [],
    }),
    [coverage, documentsRelated, commercial]
  );

  const methods = useForm({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const { control, handleSubmit } = methods;

  /**
   * --------------------------------------------------------------------------
   * Binary helpers
   * --------------------------------------------------------------------------
   */
  const createBinaryCustom = async (data: FormValues): Promise<any[]> => {
    if (!data.files?.length) {
      return [];
    }

    return Promise.all(
      data.files.map(async (file: Blob) => {
        const base64 = await convertBlobToBase64(file);
        const { name } = getFileData(Object.assign(file));

        const binary = await handleCreateBynary({
          resourceType: 'Binary',
          contentType: file.type,
          securityContext: {
            reference: getResourceReference(patient),
          },
          data: base64,
        });

        return {
          ...binary[0],
          name,
        };
      })
    );
  };

  /**
   * --------------------------------------------------------------------------
   * DocumentReference creation
   * --------------------------------------------------------------------------
   */

  const getOrganizationReference = () => {
    const selectedOrganization = organization?.find(
      (item) => item.id === authUser.organization_fhir_uri
    );

    if (!selectedOrganization) {
      return undefined;
    }

    return {
      display: selectedOrganization.name,
      reference: `Organization/${selectedOrganization.id}`,
    };
  };

  const getAuthorReference = () => {
    const userFhirUri = authUser.user_fhir_uri?.trim();

    if (!userFhirUri) {
      return {
        display: authUser.name,
      };
    }

    return {
      reference: `Practitioner/${getFhirIdFromEntity(userFhirUri)}`,
      display: authUser.name,
    };
  };

  const createDocumentReference = async (binaryList: any[]): Promise<DocumentReference[]> => {
    if (!binaryList.length) {
      return [];
    }

    const organizationFhir = getOrganizationReference();

    const patientReference = getResourceReference(patient);

    const now = new Date().toISOString();

    const documentReferences = binaryList.map((binary) => ({
      resourceType: 'DocumentReference',
      status: 'current',

      subject: {
        reference: patientReference,
      },

      author: [getAuthorReference()],

      ...(organizationFhir && {
        custodian: organizationFhir,
      }),

      date: now,

      content: [
        {
          attachment: {
            contentType: binary.contentType,
            creation: now,
            title: binary.name,
            url: `${configurations?.fhir?.baseUrl}/Binary/${binary.id}`,
          },
        },
      ],
    }));

    return (await handleCreateDocumentReference(documentReferences)) ?? [];
  };

  const mapCoverage = (data: any) => {
    const { insuranceId, insuranceCompany, groupNumber, expirationDate } = data;

    return {
      resourceType: 'Coverage',

      ...(coverage?.id && {
        id: coverage.id,
      }),

      status: 'active',

      subscriberId: insuranceId,

      beneficiary: {
        reference: getResourceReference(patient),
      },

      payor: [{ display: `${option}_${insuranceCompany}` }],

      class: [
        {
          type: {
            coding: [
              {
                system: COVERAGE_CLASS_SYSTEM,
                code: COVERAGE_CLASS_CODE,
              },
            ],
          },

          value: groupNumber,

          name: startCase(lowerCase(option)),
        },
      ],

      period: {
        end: expirationDate.toISOString(),
      },
    };
  };

  /**
   * --------------------------------------------------------------------------
   * DocumentReference mapping
   * --------------------------------------------------------------------------
   */

  const updateDocumentRelationships = (
    documents: FormValues['documents'],
    createdDocuments: DocumentReference[] = [],
    resourceToRelate?: any
  ) => {
    const selectedDocumentIds = new Set(documents ?? []);

    const previousDocumentIds = new Set(documentsRelated);

    const documentsToRemove = new Set(
      documentsRelated.filter((documentId) => !selectedDocumentIds.has(documentId))
    );

    const documentsToAdd = new Set(
      Array.from(selectedDocumentIds).filter((documentId) => !previousDocumentIds.has(documentId))
    );

    const removedDocuments =
      documentReference
        ?.filter((document) => (document.id ? documentsToRemove.has(document.id) : false))
        .map((document) => removeDocumentRelationships(document, resourceToRelate)) ?? [];

    const selectedDocuments =
      documentReference
        ?.filter((document) => (document.id ? documentsToAdd.has(document.id) : false))
        .map((document) => addDocumentRelationships(document, resourceToRelate)) ?? [];

    const newDocuments = createdDocuments.map((document) =>
      addDocumentRelationships(document, resourceToRelate)
    );

    return [...removedDocuments, ...selectedDocuments, ...newDocuments];
  };

  /**
   * --------------------------------------------------------------------------
   * Document change detection
   * --------------------------------------------------------------------------
   */

  const hasDocumentsChanged = (data: FormValues): boolean =>
    !isEqual(data.documents ?? [], documentsRelated) || Boolean(data.files?.length);

  /**
   * --------------------------------------------------------------------------
   * Document update flow
   * --------------------------------------------------------------------------
   */

  const updateDocumentsSelected = async (
    data: FormValues,
    createdDocs: DocumentReference[] = [],
    resourceToRelate?: any
  ): Promise<DocumentReference[]> => {
    const selectedDocuments = data.documents ?? [];

    const requiredReferences = getRelatedReferences(resourceToRelate, serviceRequest);

    const allUpdatedDocuments = updateDocumentRelationships(
      selectedDocuments,
      createdDocs,
      requiredReferences
    );

    if (allUpdatedDocuments.length > 0) {
      await handleUpdateDocumentReference(allUpdatedDocuments);
    }

    /**
     * ServiceRequest.supportingInfo should contain
     * the documents that remain related to the
     * ServiceRequest.
     */
    if (serviceRequest && typeof handleUpdateResource === 'function') {
      const removedDocuments = allUpdatedDocuments.filter((document) => {
        const removedIds = new Set(
          documentsRelated.filter((id) => !selectedDocuments.includes(id))
        );

        return Boolean(document.id && removedIds.has(document.id));
      });

      const selectedAndCreatedDocuments = allUpdatedDocuments.filter((document) => {
        const documentId = document.id;

        return documentId && selectedDocuments.includes(documentId);
      });

      const coverageReferences = getCoverageReferences(resourceToRelate);

      const supportingInfo = mapDocumentReferenceToResource(
        serviceRequest,
        [...selectedAndCreatedDocuments, ...createdDocs],
        removedDocuments
      );

      const updatedServiceRequest = {
        ...serviceRequest,
        ...(supportingInfo.length > 0
          ? {
              supportingInfo,
            }
          : {
              supportingInfo: undefined,
            }),

        ...(coverageReferences.length > 0
          ? {
              insurance: coverageReferences,
            }
          : {
              insurance: undefined,
            }),
      };

      if (!supportingInfo.length) {
        delete updatedServiceRequest.supportingInfo;
      }

      if (!coverageReferences.length) {
        delete updatedServiceRequest.insurance;
      }

      handleUpdateResource(updatedServiceRequest);
    }

    return allUpdatedDocuments;
  };

  /**
   * --------------------------------------------------------------------------
   * Submit
   * --------------------------------------------------------------------------
   */

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setOpenBackdrop(true);

    try {
      /**
       * 1. Create Binary resources
       */
      const binaryList = await createBinaryCustom(data);

      /**
       * 2. Create DocumentReference resources
       */
      const createdDocuments =
        binaryList.length > 0 ? await createDocumentReference(binaryList) : [];

      /**
       * 3. Create/update Coverage
       */
      const coverageResource = mapCoverage(data);

      let resourceToRelate: any;

      if (coverageResource) {
        resourceToRelate = await handleCoverage(coverageResource);
      }

      /**
       * 4. Update ServiceRequest
       *
       * This needs to happen when:
       * - documents changed
       * - new documents were created
       * - coverage was created/updated
       */
      const shouldUpdateServiceRequest =
        hasDocumentsChanged(data) || createdDocuments.length > 0 || coverageResource;

      if (shouldUpdateServiceRequest) {
        await updateDocumentsSelected(data, createdDocuments, resourceToRelate);
      }

      onCancel();
    } catch (error) {
      console.error('Error saving Commercial information:', error);

      enqueueSnackbar(i18n('errorMessages.error', 'crs'), {
        variant: 'error',
      });
    } finally {
      setOpenBackdrop(false);
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------------
   */

  return (
    // @ts-ignore
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Stack>
        <Grid container spacing={3} sx={{ p: 2 }}>
          <Grid item xs={6}>
            <Stack direction="column" alignItems="left" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {i18n('patients.details.insurance.insuranceCompany', 'crs')}*
              </Typography>
              <RHFTextField name="insuranceCompany" label="" />
            </Stack>
            <Stack direction="column" alignItems="left" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {i18n('patients.details.insurance.policeNumber', 'crs')}*
              </Typography>
              <RHFTextField name="insuranceId" label="" />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack direction="column" alignItems="left" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {i18n('patients.details.insurance.expirationDate', 'crs')}*
              </Typography>
              <Controller
                name="expirationDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" alignItems="left" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {i18n('patients.details.insurance.groupNumber', 'crs')}*
              </Typography>
              <RHFTextField name="groupNumber" label="" />
            </Stack>
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
          {i18n('patients.details.insurance.insuranceCard', 'crs')}
        </Typography>
        <UploadFiles
          label="insurance"
          restrictFiles={true}
          documentReferences={documentReference}
          patient={patient}
          docRelated={documentsRelated}
        />
      </Stack>
      <Stack alignItems="center">
        <DialogActions>
          <Button variant="outlined" color="info" type="submit">
            {i18n('saveChanges')}
          </Button>
        </DialogActions>
      </Stack>
    </FormProvider>
  );
}
